import PageLayout from '../../components/PageLayout';
import useSWR from 'swr';
import { useHistory } from 'react-router-dom';
import {
  formatCurrencyV2,
  formatDate,
  getItemById,
  getQueryString,
  makeDropdownOptions,
  OPTIONS,
  useReducerSpread,
} from '../../utils/common.util';

import {
  Checkbox,
  Select,
  SelectItem,
  TextInput,
} from 'carbon-components-react';
import CustomButton from '../../components/custom-button/custom-btn.component';
import { useEffect, useState } from 'react';
import { useCustomMutation } from '../../utils/useCustomMutation';
import { postMethod } from '../../api/common';
import { useMemo } from 'react';
import { ConfirmModal } from '../../components/modal/confirm-modal/ConfirmModal.component';
import { useClassAccessList } from '../../api/class.service';
import { useUrlState } from '../../utils/useUrlState';
import { useQueryParams } from '../../utils/useQueryParams';
import { StudentSearchField } from '../../components/student-search/StudentSearchField';
import { filterStudentsBySearchTerm } from '../../utils/student-search.util';

export function calculatePaymentBreakdown({
  totalPayment,
  totalArrears,
  currentAmount,
  depositBalance,
  attendanceStatus,
}) {
  let remaining = totalPayment || 0;
  let payArrears = 0;
  let amountPaid = 0;
  let payAdvance = 0;

  if (attendanceStatus === 'Absent') {
    amountPaid = 0;
    payAdvance = 0;
    payArrears = 0;
    remaining = 0;
  } else {
    // Priority 1: Pay arrears first
    payArrears = remaining >= totalArrears ? totalArrears : remaining;
    remaining -= payArrears;

    // Priority 2: Pay for the day (fee amount)
    if (depositBalance) {
      amountPaid =
        depositBalance > currentAmount
          ? 0
          : currentAmount - (depositBalance || 0);
    } else {
      if (totalArrears) {
        amountPaid = remaining > 0 ? Math.min(remaining, currentAmount) : 0;
      } else {
        amountPaid = Math.min(currentAmount, remaining);
      }
    }

    // Priority 3: Pay advance (remaining goes to advance)
    payAdvance = remaining > amountPaid ? remaining - amountPaid : 0;
  }

  const depositPaymentAmount = Math.min(depositBalance || 0, currentAmount);

  const paymentMode = totalPayment
    ? 'Cash'
    : depositPaymentAmount
      ? 'DepositDeduction'
      : '';

  return {
    payArrears,
    amountPaid,
    payAdvance,
    paymentMode,
    depositPaymentAmount,
    totalArrears: totalArrears || 0,
  };
}

export function CollectDailyFeesPage() {
  const history = useHistory();

  const {
    feeTypeId: urlDailyBillingFeeId,
    billDate: urlPaymentDate,
    debitAccountId: urlDebitAccountId,
    classId: urlClassId,
    genericGroupingId: urlGenericGroupingId,
  } = useQueryParams(
    'feeTypeId',
    'classId',
    'billDate',
    'debitAccountId',
    'genericGroupingId',
  );

  if (!urlDailyBillingFeeId || !urlPaymentDate || !urlDebitAccountId) {
    history.push('/daily-fees');
  }

  const [query, setQuery] = useUrlState({
    classId: urlClassId,
    genericGroupingId: urlGenericGroupingId,
  });

  const [formData, setFormData] = useReducerSpread({
    ledgerAccountId: null,
    revenues: [],
  });

  const singleFeeTypeUrl = `daily-billing/fees/${urlDailyBillingFeeId}/summary`;
  const { data: d, isLoading: isFeeTyping } = useSWR(singleFeeTypeUrl);
  const feeType = d?.dailyBillingFee ?? null;
  const isGeneric = feeType?.type === 'Generic';

  const { classAccessList } = useClassAccessList();

  const classObj = feeType?.classAmounts?.find(
    (cls) => cls?.classId === parseFloat(query.classId),
  );

  const group = feeType?.genericAmounts?.find(
    (cls) => cls?.id === parseFloat(query.genericGroupingId),
  );

  const currentAmount = classObj?.amount || group?.amount;

  const currentLedgerAccountId =
    classObj?.dailyBillingFee?.ledgerAccountId ||
    group?.dailyBillingFee?.ledgerAccountId;

  const { data, isLoading } = useSWR(
    feeType
      ? `daily-billing/fees/student/list?${getQueryString({
          classId: query.classId,
          genericGroupingId: query.genericGroupingId,
          billDate: urlPaymentDate,
          dailyBillingFeeId: urlDailyBillingFeeId,
        })}`
      : null,
  );
  const students = useMemo(() => data?.students ?? [], [data]);

  useEffect(() => {
    if (students.length > 0) {
      setFormData({
        ledgerAccountId: currentLedgerAccountId,
        revenues: students.map((student) => {
          const totalArrears = student.arrearsBalance || 0;
          const depositBalance = student.depositBalance || 0;
          const attendanceStatus = student?.attendanceStatus || 'Absent';
          const initialTotalPayment =
            attendanceStatus === 'Absent'
              ? 0
              : depositBalance > currentAmount
                ? 0
                : currentAmount - (depositBalance || 0);
          const breakdown = calculatePaymentBreakdown({
            totalPayment: initialTotalPayment,
            totalArrears,
            currentAmount,
            depositBalance,
            attendanceStatus,
          });

          const initialPayment = {
            studentId: student?.student?.id,
            uniqueId: student?.student?.studentId,
            firstName: student?.student?.firstName || '',
            lastName: student?.student?.lastName || '',
            class: student?.student?.classDescription || '',
            attendanceStatus,
            depositBalance,
            totalArrears,
            totalPayment: initialTotalPayment,
            ...breakdown,
            studentBillId: student?.dailyFeeStudentBillItem?.studentBillId,
          };
          return initialPayment;
        }),
      });
    }
  }, [currentAmount, setFormData, students, currentLedgerAccountId]);

  const isAttendanceMarked = !!students?.[0]?.attendanceStatus;

  const { trigger, isMutating, error } = useCustomMutation(
    `daily-billing/revenue/new/v2`,
    (url, { arg }) => postMethod(url, arg),
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      dailyBillingFeeId: urlDailyBillingFeeId,
      paymentDate: urlPaymentDate,
      debitAccountId: urlDebitAccountId,
      classId: query.classId,
      genericGroupingId: query.genericGroupingId,
    };

    trigger(payload, {
      onSuccess: () => {
        setisSaved(true);
        setHasChanged(false);
        history.push(
          `/daily-fees/edit?${getQueryString({
            classId: query.classId,
            genericGroupingId: query.genericGroupingId,
            dailyBillingFeeId: urlDailyBillingFeeId,
            dateFrom: urlPaymentDate,
            debitAccountId: urlDebitAccountId,
          })}`,
        );
      },
    });
  };

  const { data: ledgerAccounts } = useSWR('chart-of-accounts/ledger-accounts');

  const [isOpen, setIsOpen] = useState('');
  const [hasChanged, setHasChanged] = useState(false);
  const [isSaved, setisSaved] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [classSearchTerm, setClassSearchTerm] = useState('');
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('');
  const [bulkAttendanceStatus, setBulkAttendanceStatus] = useState('Absent');

  const {
    trigger: submit,
    isMutating: isSubmitting,
    error: submissionError,
  } = useCustomMutation(`daily-billing/revenue/approve/v2`, (url, { arg }) =>
    postMethod(url, arg),
  );

  const { data: s, isLoading: isLoadingAdditionalStudents } = useSWR(
    `daily-billing/fees/assigned-students?${getQueryString({
      dailyBillingFeeId: urlDailyBillingFeeId,
      assignedToDailyBillingFee: false,
      classId: query.classId,
      genericGroupingId: query.genericGroupingId,
    })}`,
  );

  const handleBulkAttendanceStatusChange = (newStatus) => {
    setBulkAttendanceStatus(newStatus);
    setHasChanged(true);
    const updatedRevenues = formData.revenues.map((entry) => {
      const totalPayment = newStatus === 'Absent' ? 0 : currentAmount;
      return {
        ...entry,
        attendanceStatus: newStatus,
        totalPayment,
        ...calculatePaymentBreakdown({
          totalPayment,
          totalArrears: entry.totalArrears,
          currentAmount,
          depositBalance: entry.depositBalance,
          attendanceStatus: newStatus,
        }),
      };
    });
    setFormData({ revenues: updatedRevenues });
  };
  const additionalStudents =
    s?.response?.map((student) => {
      const attendanceStatus = 'Absent';
      const totalPayment = 0;
      return {
        studentId: student.id,
        uniqueId: student.studentId,
        firstName: student.firstName,
        lastName: student.lastName,
        classDescription: student.classDescription,
        attendanceStatus,
        depositBalance: 0,
        depositPaymentAmount: 0,
        amountPaid: 0,
        totalArrears: 0,
        totalPayment,
        payArrears: 0,
        payAdvance: 0,
        studentBillId: null,
        paymentMode: '',
      };
    }) ?? [];

  const visibleRevenues = useMemo(() => {
    let results = filterStudentsBySearchTerm(
      formData.revenues,
      studentSearchTerm,
    );
    if (classSearchTerm) {
      const selectedClass = classAccessList?.find(
        (c) => String(c.id) === String(classSearchTerm),
      );
      if (selectedClass) {
        results = results.filter(
          (item) => item.class === selectedClass.description,
        );
      }
    }
    if (attendanceStatusFilter) {
      results = results.filter(
        (item) => item.attendanceStatus === attendanceStatusFilter,
      );
    }
    return results;
  }, [
    formData.revenues,
    studentSearchTerm,
    classSearchTerm,
    attendanceStatusFilter,
    classAccessList,
  ]);

  const statsItems = [
    { label: 'Amount', value: formatCurrencyV2(currentAmount) },
    {
      label: 'Debit Account',
      value: getItemById(ledgerAccounts, urlDebitAccountId)?.description,
    },
    { label: 'Payment Date', value: formatDate(urlPaymentDate) },
  ];
  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Daily Fees', to: '/daily-fees' },
        { label: `Collect Daily Fees`, active: true },
      ]}
      title={`Collect ${feeType?.name || 'Daily Fee'}`}
      className="fee-type-page"
      isLoading={isLoading || isFeeTyping || isLoadingAdditionalStudents}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px',
          borderBottom: '1px solid #ccc',
        }}
      >
        <div>
          {!isGeneric && (
            <Select
              style={{ minWidth: '200px' }}
              labelText="Class"
              value={query.classId}
              onChange={(e) => setQuery({ classId: e.target.value })}
            >
              <SelectItem disabled value="" text="Select Class" />
              {feeType?.classAmounts?.map((item) => (
                <SelectItem
                  key={item.classId}
                  text={item.classDescription}
                  value={item.classId}
                />
              ))}
            </Select>
          )}

          {isGeneric && (
            <Select
              style={{ minWidth: '200px' }}
              labelText="Group"
              value={query.genericGroupingId}
              onChange={(e) => setQuery({ genericGroupingId: e.target.value })}
            >
              <SelectItem disabled value="" text="Select Group" />
              {feeType?.genericAmounts?.map((item) => (
                <SelectItem key={item.id} text={item.name} value={item.id} />
              ))}
            </Select>
          )}
        </div>
        {!isAttendanceMarked && (
          <div>
            <Select
              style={{ minWidth: '200px' }}
              labelText="Mark All Students"
              value={bulkAttendanceStatus}
              onChange={(e) => handleBulkAttendanceStatusChange(e.target.value)}
            >
              {makeDropdownOptions(['Present', 'Absent']).map((item) => (
                <SelectItem
                  key={item.value}
                  text={item.label}
                  value={item.value}
                />
              ))}
            </Select>
          </div>
        )}

        {statsItems.map((item) => (
          <div key={item.label}>
            <p className="text-14 text-grey-light">{item.label}</p>
            <h3 className="h-14">{item.value}</h3>
          </div>
        ))}
      </div>

      {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
      {formData.revenues?.length === 0 && (
        <p className="text-14 mt-30 text-grey">
          No students available for collection.
        </p>
      )}
      {formData.revenues?.length > 0 && (
        <form onSubmit={handleSubmit}>
          <div
            className="d-flex gap-10 mt-20"
            style={{ alignItems: 'flex-end' }}
          >
            {isGeneric && (
              <div>
                <Select
                  style={{ minWidth: '200px' }}
                  labelText="Class"
                  onChange={(e) => setClassSearchTerm(e.target.value)}
                  value={classSearchTerm}
                >
                  <SelectItem value="" text="All" />
                  {classAccessList?.map((item) => (
                    <SelectItem
                      key={item.id}
                      value={item.id}
                      text={item.description}
                    />
                  ))}
                </Select>
              </div>
            )}
            <div className="mt-30" style={{ flex: 1 }}>
              <StudentSearchField
                id="collect-daily-fees-search"
                value={studentSearchTerm}
                onChange={(e) => setStudentSearchTerm(e.target.value)}
                placeholder="Search by student name"
              />
            </div>
            <div>
              <Select
                style={{ minWidth: '200px' }}
                labelText="Attendance"
                onChange={(e) => setAttendanceStatusFilter(e.target.value)}
                value={attendanceStatusFilter}
              >
                <SelectItem value="" text="All" />
                {makeDropdownOptions(['Present', 'Absent']).map((item) => (
                  <SelectItem
                    key={item.value}
                    text={item.label}
                    value={item.value}
                  />
                ))}
              </Select>
            </div>
          </div>

          {(studentSearchTerm || classSearchTerm || attendanceStatusFilter) &&
            visibleRevenues.length === 0 && (
              <p className="text-14 mt-20 text-grey">
                No students match your search.
              </p>
            )}

          <div style={{ overflow: 'auto' }} className="table-container mt-30">
            <table className="bx--data-table table-white">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Class</th>
                  <th>Status</th>
                  <th>
                    Total arrears <br />
                    (GH₵)
                  </th>
                  <th>
                    Pay arrears <br />
                    (GH₵)
                  </th>
                  <th>
                    Deposit balance <br />
                    (GH₵)
                  </th>
                  <th>
                    Deposit allocated <br />
                    (GH₵)
                  </th>
                  <th>
                    Pay deposit <br />
                    (GH₵)
                  </th>
                  <th>
                    Today <br />
                    (GH₵)
                  </th>
                  <th>
                    Total payment <br />
                    (GH₵)
                  </th>
                  <th>Payment Mode</th>
                </tr>
              </thead>
              <tbody>
                {visibleRevenues.map((entry) => (
                  <tr key={entry.studentId}>
                    <td>
                      {entry.firstName} {entry.lastName}
                    </td>
                    <td>{entry?.class || entry?.classDescription}</td>
                    <td>
                      {students?.find(
                        (student) => student.student.id === entry.studentId,
                      )?.attendanceStatus || (
                        <Select
                          style={{ minWidth: '130px' }}
                          labelText=""
                          value={entry.attendanceStatus}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            const totalPayment =
                              newStatus === 'Absent' ? 0 : currentAmount;
                            setHasChanged(true);
                            setFormData({
                              revenues: formData.revenues.map((ent) =>
                                ent.studentId === entry.studentId
                                  ? {
                                      ...ent,
                                      attendanceStatus: newStatus,
                                      totalPayment,
                                      ...calculatePaymentBreakdown({
                                        totalPayment,
                                        totalArrears: ent.totalArrears,
                                        currentAmount,
                                        depositBalance: ent.depositBalance,
                                        attendanceStatus: newStatus,
                                      }),
                                    }
                                  : ent,
                              ),
                            });
                          }}
                        >
                          {makeDropdownOptions(['Present', 'Absent']).map(
                            (item) => (
                              <SelectItem
                                key={item.value}
                                text={item.label}
                                value={item.value}
                              />
                            ),
                          )}
                        </Select>
                      )}
                    </td>
                    <td>{entry.totalArrears}</td>
                    <td>{entry.payArrears}</td>
                    <td>{entry.depositBalance}</td>
                    <td>
                      {entry.attendanceStatus === 'Absent'
                        ? ''
                        : entry.depositPaymentAmount}
                    </td>
                    <td>{entry.payAdvance}</td>
                    <td>{entry.amountPaid}</td>
                    <td style={{ width: '160px' }}>
                      <TextInput
                        id={`totalPayment-${entry.studentId}`}
                        name={`totalPayment-${entry.studentId}`}
                        type="number"
                        labelText=""
                        placeholder=""
                        disabled={entry.attendanceStatus === 'Absent'}
                        value={
                          entry.attendanceStatus === 'Absent'
                            ? ''
                            : entry.totalPayment
                        }
                        onChange={(e) => {
                          const totalPayment = Math.max(
                            0,
                            parseFloat(e.target.value) || 0,
                          );
                          setHasChanged(true);
                          setFormData({
                            revenues: formData.revenues.map((ent) =>
                              ent.studentId === entry.studentId
                                ? {
                                    ...ent,
                                    totalPayment,
                                    ...calculatePaymentBreakdown({
                                      totalPayment,
                                      totalArrears: ent.totalArrears,
                                      currentAmount,
                                      depositBalance: ent.depositBalance,
                                      attendanceStatus: ent.attendanceStatus,
                                    }),
                                  }
                                : ent,
                            ),
                          });
                        }}
                      />
                    </td>
                    <td style={{ width: '250px' }}>
                      <Select
                        style={{ minWidth: '130px' }}
                        id="paymentMode"
                        labelText=""
                        disabled={entry.attendanceStatus === 'Absent'}
                        value={
                          entry.attendanceStatus === 'Absent'
                            ? ''
                            : entry.paymentMode
                        }
                        onChange={(e) => {
                          const newMode = e.target.value;
                          setHasChanged(true);
                          setFormData({
                            revenues: formData.revenues.map((ent) =>
                              ent.studentId === entry.studentId
                                ? {
                                    ...ent,
                                    paymentMode:
                                      ent.attendanceStatus === 'Absent'
                                        ? ''
                                        : newMode,
                                  }
                                : ent,
                            ),
                          });
                        }}
                      >
                        <SelectItem disabled value="" text="" />
                        {OPTIONS.paymentModes
                          .filter((mode) => mode.value !== 'Cheque')
                          .map((mode) => (
                            <SelectItem
                              key={mode.value}
                              value={mode.value}
                              text={mode.label}
                            />
                          ))}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold">
                  <td colSpan="3">Total</td>
                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) => acc + parseFloat(entry.totalArrears || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) => acc + parseFloat(entry.payArrears || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) =>
                        acc + parseFloat(entry.depositBalance || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) =>
                        acc + parseFloat(entry.depositPaymentAmount || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) => acc + parseFloat(entry.payAdvance || 0),
                      0,
                    )}
                  </td>

                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) => acc + parseFloat(entry.amountPaid || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) => acc + parseFloat(entry.totalPayment || 0),
                      0,
                    )}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {isSaved && (
            <div className="mt-20 text-success">Data saved as draft</div>
          )}
          <div className="mt-40">
            <p className="text-12" style={{ justifySelf: 'end' }}>
              Please "Save" changes as draft before you "SUBMIT"!
            </p>
            <div
              style={{
                marginTop: '10px',
                display: 'flex',
                gap: '10px',
                justifyContent: 'end',
              }}
            >
              <CustomButton
                type="submit"
                classes={`secondary font-bold`}
                isLoading={isMutating}
                onClick={handleSubmit}
                disabled={
                  !formData.revenues.length ||
                  formData.revenues.some(
                    (entry) =>
                      !entry.studentId ||
                      Number(entry.totalPayment) < 0 ||
                      !entry.attendanceStatus,
                  )
                }
              >
                Save AS DRAFT
              </CustomButton>
              <CustomButton
                type="button"
                classes={`primary font-bold`}
                disabled={isMutating || !isSaved || hasChanged}
                isLoading={isSubmitting}
                onClick={() => setIsOpen('submit')}
              >
                SUBMIT
              </CustomButton>
            </div>
          </div>
        </form>
      )}

      {additionalStudents.length > 0 && (
        <AdditionalStudents
          students={additionalStudents}
          formDataRevenues={formData.revenues}
          setFormData={setFormData}
          currentAmount={currentAmount}
        />
      )}

      <ConfirmModal
        title="Are you sure you want to sumit?"
        isOpen={isOpen === 'submit'}
        closeModal={() => setIsOpen('')}
        description={
          <>
            <p className="text-label">
              Are you very sure you want to submit this collection? You can not
              edit it after submission.
            </p>
          </>
        }
        actionText="SUBMIT"
        onClick={() =>
          submit(
            {
              ...formData,
              dailyBillingFeeId: urlDailyBillingFeeId,
              paymentDate: urlPaymentDate,
              debitAccountId: urlDebitAccountId,
              classId: query.classId,
              genericGroupingId: query.genericGroupingId,
            },
            {
              onSuccess: () => {
                history.push(
                  `/daily-fees?${getQueryString({
                    dailyBillingFeeId: urlDailyBillingFeeId,
                    paymentDate: urlPaymentDate,
                    debitAccountId: urlDebitAccountId,
                    classId: query.classId,
                    genericGroupingId: query.genericGroupingId,
                  })}`,
                );
              },
            },
          )
        }
        isLoading={isSubmitting}
        errorMessage={submissionError ? submissionError.message : null}
        actionBtnClasses="primary"
      />
    </PageLayout>
  );
}

export function AdditionalStudents({
  students,
  formDataRevenues,
  setFormData,
  currentAmount,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  // selectedAdditionals are the ones from students that are in formDataRevenues
  const selectedAdditionals = formDataRevenues.filter((r) =>
    students.some((s) => s.studentId === r.studentId),
  );

  const filteredStudents = useMemo(
    () => filterStudentsBySearchTerm(students, searchTerm),
    [searchTerm, students],
  );

  return (
    <div className="mb-50">
      <h4 className="font-bold mt-50">Addtional Students</h4>
      <p className="text-12 mt-10">
        Select students who are not under this fee type
      </p>

      <StudentSearchField
        id="additional-students-search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="my-12"
      />

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <div>
          <Checkbox
            id="additional-students"
            name="additional-students"
            checked={
              selectedAdditionals.length > 0 &&
              selectedAdditionals.length === students?.length
            }
            indeterminate={
              selectedAdditionals.length > 0 &&
              selectedAdditionals.length < students?.length
            }
            onChange={() => {
              if (selectedAdditionals.length > 0) {
                setFormData({
                  revenues: formDataRevenues.filter(
                    (r) => !students.some((s) => s.studentId === r.studentId),
                  ),
                });
              } else {
                setFormData({
                  revenues: [
                    ...formDataRevenues,
                    ...students.map((student) => ({
                      ...student,
                      totalArrears: 0,
                      totalPayment: currentAmount,
                      depositPaymentAmount: 0,
                      amountPaid: currentAmount,
                      payArrears: 0,
                      payAdvance: 0,
                    })),
                  ],
                });
              }
            }}
          />
        </div>
        <span>All</span>
      </div>

      <div
        style={{
          overflow: 'auto',
          height: '350px',
          border: '1px solid #ccc',
          padding: '16px',
          borderRadius: '10px',
          marginTop: '4px',
        }}
      >
        {filteredStudents.map((student) => (
          <div
            key={student.studentId}
            style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
          >
            <div>
              <Checkbox
                id={`add-students-${student.studentId}`}
                name={`add-students-${student.studentId}`}
                checked={formDataRevenues.some(
                  (item) => item.studentId === student.studentId,
                )}
                onChange={() => {
                  if (
                    formDataRevenues.some(
                      (item) => item.studentId === student.studentId,
                    )
                  ) {
                    const filtered = formDataRevenues.filter(
                      (item) => item.studentId !== student.studentId,
                    );
                    setFormData({
                      revenues: filtered,
                    });
                  } else {
                    setFormData({
                      revenues: [
                        ...formDataRevenues,
                        {
                          ...student,
                          totalArrears: 0,
                          totalPayment: currentAmount,
                          depositPaymentAmount: 0,
                          amountPaid: currentAmount,
                          payArrears: 0,
                          payAdvance: 0,
                        },
                      ],
                    });
                  }
                }}
              />
            </div>
            <div>
              {student.firstName} {student.lastName}
            </div>
            <span>-</span>
            <span>{student.classDescription}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
